import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackTop from "@/components/feature/BackTop";
import { usePageMeta } from "@/hooks/usePageMeta";
import { news, site } from "@/mocks/content";
import { GridCard, catOf } from "@/components/feature/Cards";
import { formatCount } from "@/utils/format";

function Related({ id, category }: { id: number; category: string }) {
  const picks = useMemo(() => {
    const sameCat = news.filter((n) => n.category === category && n.id !== id);
    const others = news.filter((n) => n.category !== category && n.id !== id);
    return [...sameCat, ...others].slice(0, 4);
  }, [id, category]);
  return (
    <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
            <span className="inline-block h-3 w-1 rounded-full bg-primary-500" />
            Keep Reading
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-900">
            你可能还想吃的瓜
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {picks.map((item) => (
          <GridCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}

export default function NewsPage() {
  const { id = "" } = useParams();
  const num = Number(id);
  const item = news.find((n) => n.id === num);
  const [liked, setLiked] = useState(false);
  const [toast, setToast] = useState("");

  const valid = Boolean(item);
  usePageMeta(
    valid ? `${item.title} | 今日新瓜` : "内容不存在 | 今日新瓜",
    valid ? item.lead : "您访问的内容不存在，欢迎回到今日新瓜首页探索独家内容。",
  );

  if (!item) {
    return (
      <>
        <Navbar />
        <main className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-600">
            <i className="ri-file-forbid-line" />
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900">
            这篇内容已经下架了
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            可能是被删除或链接有误，试试从热门栏目重新开始吃瓜。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-600 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700"
          >
            返回首页
          </Link>
        </main>
        <Footer />
        <BackTop />
      </>
    );
  }

  const cat = catOf(item.category);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制，欢迎分享给瓜友");
    } catch {
      setToast("复制失败，请手动复制地址栏");
    }
    setTimeout(() => setToast(""), 2200);
  }

  const schemaArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: item.title,
    description: item.lead,
    image: item.cover,
    articleSection: cat.name,
    inLanguage: "zh-CN",
    author: { "@type": "Organization", name: item.author, email: site.email },
    publisher: { "@type": "Organization", name: site.name },
    keywords: item.tags.join(","),
    datePublished: "2026-09-06",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: cat.name, item: `/category/${cat.slug}` },
        { "@type": "ListItem", position: 3, name: item.title },
      ],
    },
  };

  return (
    <>
      <JsonLd data={schemaArticle} />
      <Navbar />
      <main>
        <div className="mx-auto max-w-4xl px-4 pt-6 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${cat.slug}`} className="transition-colors hover:text-primary-600">
              {cat.name}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="truncate text-foreground-400">{item.title}</span>
          </nav>

          <article className="pt-5">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800 hover:bg-secondary-200"
                >
                  <i className={cat.icon} />
                  {cat.name}
                </Link>
                {item.exclusive && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-background-50">
                    <i className="ri-flashlight-fill" /> 独家内幕
                  </span>
                )}
                {item.hot && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                    <i className="ri-fire-fill" /> 热门
                  </span>
                )}
              </div>
              <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-900 md:text-4xl">
                {item.title}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
                {item.lead}
              </p>
            </header>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-background-200 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                  {item.author.slice(0, 1)}
                </span>
                <div className="text-sm">
                  <p className="font-semibold text-foreground-900">{item.author}</p>
                  <p className="text-xs text-foreground-500">{item.time}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setLiked((v) => !v)}
                  aria-label="点赞"
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    liked
                      ? "bg-primary-600 text-background-50"
                      : "border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  <i className={liked ? "ri-heart-fill" : "ri-heart-line"} />
                  {formatCount(item.likes + (liked ? 1 : 0))}
                </button>
                <button
                  onClick={copyLink}
                  aria-label="分享"
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-200 px-3.5 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-700"
                >
                  <i className="ri-share-forward-line" /> 分享
                </button>
              </div>
            </div>

            <figure className="my-6 overflow-hidden rounded-2xl">
              <img
                src={item.cover}
                alt={`${item.title} 独家内容配图`}
                className="w-full object-cover"
              />
              <figcaption className="mt-2 px-1 text-center text-xs text-foreground-400">
                今日新瓜 · {cat.name}栏目报道配图
              </figcaption>
            </figure>

            <div className="space-y-5 text-[15px] leading-[1.9] text-foreground-800 md:text-base">
              {item.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {item.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-background-200/70 px-3 py-1 text-xs text-foreground-600"
                >
                  # {t}
                </span>
              ))}
            </div>

            <aside className="mt-6 rounded-2xl border border-background-200 bg-background-100/60 p-5">
              <p className="text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-fill mr-1 text-accent-500" />
                {site.notice}
              </p>
            </aside>
          </article>
        </div>

        <Related id={item.id} category={item.category} />
      </main>
      <Footer />
      <BackTop />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          <i className="ri-checkbox-circle-fill mr-1.5 text-accent-400" />
          {toast}
        </div>
      )}
    </>
  );
}